import { useBilling } from '../contexts/BillingContext.js';
import { usePlatform } from '../contexts/PlatformContext.js';
import { useIsMobileClient } from '../hooks/useTeamsContext.js';

const SERVER_URL = import.meta.env.VITE_SERVER_URL || '';

/**
 * Slim trial / subscription status strip.
 *
 * Renders nothing in the common case (paid, or billing disabled) so it costs
 * no vertical space — the Teams side panel budget is tight enough that a
 * permanent banner would be unacceptable. See the side-panel notes in
 * CLAUDE.md: every 10 px of chrome matters here.
 */
export function TrialBanner() {
  const { entitlement, startCheckout, openBillingPortal, isStartingCheckout, actionError } =
    useBilling();
  // Teams mobile forbids upsell and payment references, so on mobile this
  // strip reports status only — no button, no price, no purchase verbs.
  const isMobile = useIsMobileClient();
  const { adapter } = usePlatform();

  if (!entitlement) return null;
  // Gates are off server-side — showing trial copy would be a lie.
  if (!entitlement.billingEnabled) return null;
  // Paid and healthy: say nothing.
  if (entitlement.status === 'active' || entitlement.status === 'comped') return null;

  // A guest in another organisation's meeting is using the host's Annoteer, so
  // trial days and Subscribe are not theirs to act on here. Offer it for their
  // own team instead. past_due falls through: that is their own subscription.
  // Not on mobile, where Teams forbids anything that leads towards a purchase.
  if (entitlement.isGuest && entitlement.status !== 'past_due') {
    if (isMobile) return null;
    return (
      <div
        role="status"
        data-guest-offer
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '3px 10px',
          background: 'var(--color-surface-raised)',
          color: 'var(--color-text)',
          fontSize: 11,
          lineHeight: 1.4,
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          You&rsquo;re using Annoteer as a guest in this meeting.
        </span>
        <button
          onClick={() =>
            void adapter.openExternalLink(`${SERVER_URL || window.location.origin}/get?from=guest`)
          }
          style={{
            flex: '0 0 auto',
            background: 'transparent',
            color: 'var(--color-text)',
            border: '1px solid var(--color-text-muted)',
            padding: '1px 10px',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Add Annoteer to your team
        </button>
      </div>
    );
  }

  // Never seen, and not a guest: no trial exists until they open a document.
  if (entitlement.status === 'none') return null;

  const { isTrial, trialDaysLeft, trialExtractionsLeft, entitled, status } = entitlement;
  const isPastDue = status === 'past_due';

  let tone: 'info' | 'warn' | 'urgent' = 'info';
  let message: string;

  if (!entitled) {
    tone = 'urgent';
    message = isMobile
      ? 'Your trial has ended. Open Annoteer on desktop or web to continue.'
      : 'Your trial has ended — subscribe to keep using Annoteer.';
  } else if (status === 'past_due') {
    tone = 'warn';
    message = isMobile
      ? 'Your subscription needs attention. Open Annoteer on desktop or web.'
      : 'Payment failed. Update your card to avoid interruption.';
  } else if (isTrial) {
    const days = trialDaysLeft === 1 ? '1 day' : `${trialDaysLeft} days`;
    const extractions =
      trialExtractionsLeft != null ? ` · ${trialExtractionsLeft} AI extractions left` : '';
    tone = trialDaysLeft <= 3 ? 'warn' : 'info';
    message = `Trial: ${days} left${extractions}`;
  } else {
    return null;
  }

  const palette = {
    info: { bg: 'var(--color-surface-raised)', fg: 'var(--color-text)' },
    warn: { bg: '#3b2f13', fg: '#fcd34d' },
    urgent: { bg: '#4b1d1d', fg: '#fca5a5' },
  }[tone];

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: '3px 10px',
        background: palette.bg,
        color: palette.fg,
        fontSize: 11,
        lineHeight: 1.4,
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {actionError ?? message}
      </span>
      {/* past_due means a subscription already exists and its payment failed —
          the fix is a new card in the billing portal, NOT a second checkout.
          Sending them to Checkout would create a duplicate subscription and
          bill them twice while the original stayed unpaid.

          Suppressed wholesale on mobile: both branches lead to a payment page,
          which Teams mobile forbids. See isMobile above. */}
      {!isMobile && (
        <button
          onClick={() => void (isPastDue ? openBillingPortal() : startCheckout())}
          disabled={isStartingCheckout}
          style={{
            flex: '0 0 auto',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: '#fff',
            border: 'none',
            padding: '2px 10px',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            cursor: isStartingCheckout ? 'default' : 'pointer',
            opacity: isStartingCheckout ? 0.6 : 1,
          }}
        >
          {isStartingCheckout ? 'Opening…' : isPastDue ? 'Update card' : 'Subscribe'}
        </button>
      )}
    </div>
  );
}
